import React, {
  cloneElement,
  isValidElement,
  PropsWithChildren,
  ReactHTMLElement,
  useEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { domToReact, attributesToProps } from 'html-react-parser'
import { kebabToCamelObject } from '@skedulo/breeze-ui-react'
import { PlatformComponent } from '../../core/components'
import { registerComponent } from '../../core/helpers'
import { withComponentErrorBoundary } from '../../errors/components/platform-error-boundary'
import { UIErrorCode } from '../../errors/constants'
import { throwPlatformError } from '../../errors/utils'

export interface HorizonElement extends HTMLElement {
  /**
   * A React component that is being transported by a Horizon Web Component.
   */
  Component?: React.ComponentType<unknown>
  /**
   * The name of the external package that the Horizon Web Component is from.
   */
  packageName?: string
}

export interface HorizonContainerProps {
  /**
   * A Horizon Web Component as a JSX Element.
   */
  component: ReturnType<typeof domToReact>
  /**
   * The Horizon Web Component's tag name.
   */
  tagName: string
}

/**
 * Checks if the element has the required properties of a Horizon Element.
 */
const isValidHorizonElement = (element: unknown): element is HorizonElement => {
  const maybeHorizonElement = element as HorizonElement

  if (!maybeHorizonElement) return false
  if (!(maybeHorizonElement instanceof HTMLElement)) return false

  return !!(maybeHorizonElement.packageName && maybeHorizonElement.Component)
}

// How many times to retry reading the Horizon Element before giving up
const maxRetries = 100

/**
 * Takes a Horizon Web Component, extracts the React component it is
 * transporting, then registers it with the platform.
 *
 * Finally, renders the registered component using `PlatformComponent` with a
 * portal into the Horizon Web Component's Shadow DOM.
 *
 * Due to the nature of how these components are loaded, we sometimes have to
 * poll for the component to be ready to register.
 */
export const HorizonContainer = withComponentErrorBoundary(
  <Props,>({
    children,
    component,
    tagName,
    ...props
  }: PropsWithChildren<HorizonContainerProps & Props>) => {
    const horizonElementRef = useRef<HorizonElement>(null)
    const [componentName, setComponentName] = useState<string>()
    const [packageName, setPackageName] = useState<string>()
    const [isRegistered, setIsRegistered] = useState(false)
    const [retriesCount, setRetriesCount] = useState(0)
    const [dynamicProps, setDynamicProps] = useState({})
    const [canRegister, setCanRegister] = useState(false)
    const [isObserved, setIsObserved] = useState(false)

    /**
     * Observe the Horizon Element for external changes to its attributes and
     * update props accordingly.
     */
    const observerRef = useRef<MutationObserver>(
      new MutationObserver(() => {
        if (!horizonElementRef.current) return

        const attributes = [...horizonElementRef.current.attributes].reduce<
          Record<string, string>
        >((map, { name, value }) => ({ ...map, [name]: value }), {})

        setDynamicProps(attributesToProps(kebabToCamelObject(attributes)))
      }),
    )

    useEffect(() => {
      const observer = observerRef.current
      return () => observer.disconnect()
    }, [])

    // Check if the Horizon Element is ready to be registered, and retry if not
    useEffect(() => {
      // Stop trying if we've hit the max retries. It's probably another issue.
      if (retriesCount > maxRetries)
        return throwPlatformError(
          `Unable to read Horizon Element ${tagName} after ${retriesCount} attempts`,
          UIErrorCode.HORIZON_COMPONENT_NOT_FOUND,
        )

      // Exit if we can already register
      if (canRegister) return

      const maybeHorizonElement = horizonElementRef.current

      if (isValidHorizonElement(maybeHorizonElement)) {
        return setCanRegister(true)
      }

      // Wait and try again if it's empty
      setTimeout(() => {
        console.log(
          `Horizon Element ${tagName} appears to be empty, retrying...`,
          { retriesCount },
        )
        return setRetriesCount(retriesCount + 1)
      }, 300)
    }, [canRegister, horizonElementRef, retriesCount, tagName])

    // Extract the package name and component from the Horizon Element transporting them
    useEffect(() => {
      // Don't try to register if the component/bundle isn't ready
      if (!canRegister) return

      const maybeHorizonElement = horizonElementRef.current

      // Shouldn't need this as canRegister should mean we're safe, but just in case
      if (!isValidHorizonElement(maybeHorizonElement)) {
        throwPlatformError(
          `Expected ${tagName} to be a valid Horizon Element. Did you include a 'packageName' and 'Component'?`,
          UIErrorCode.HORIZON_COMPONENT_INVALID,
        )
        return
      }

      setComponentName(maybeHorizonElement.Component?.name)
      setPackageName(maybeHorizonElement.packageName)
    }, [canRegister, horizonElementRef, tagName])

    // Register the component with the platform if everything else was successful and we have all the bits we need
    useEffect(() => {
      if (
        !horizonElementRef.current?.Component ||
        !componentName ||
        !packageName
      )
        return

      // Only register the component if we have everything we need
      registerComponent(
        packageName,
        componentName,
        horizonElementRef.current.Component,
      )

      setIsRegistered(true)
    }, [horizonElementRef, componentName, packageName])

    const ready = !!(
      isRegistered &&
      packageName &&
      componentName &&
      horizonElementRef.current
    )

    if (ready && !isObserved) {
      observerRef.current.observe(horizonElementRef.current, {
        characterData: true,
        attributes: true,
      })

      setIsObserved(true)
    }

    return (
      <>
        {isValidElement<ReactHTMLElement<HorizonElement>>(component) &&
          cloneElement(component, { ref: horizonElementRef })}
        {ready &&
          createPortal(
            <PlatformComponent
              packageName={packageName}
              name={componentName}
              {...props}
              {...dynamicProps}
            >
              {children}
            </PlatformComponent>,
            // React Portal doesn't recognise shadowRoot as a valid container, but it is
            horizonElementRef.current.shadowRoot as unknown as Element,
          )}
      </>
    )
  },
)
